import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import ContentEditAPI from '../../app/ContentEdit/ContentEditAPI';
import { updateOptIns } from '../../app/ContentEdit/ContentEditSlice';
import ModalDialog from '../../app/Shared/ModalDialog';
import { OptInNames } from '../../app/Shared/enums';
import Button from '../../builder-io/storyblocks-design-system/components/atoms/Button/Button';

interface AiRiskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (hasOptedIn?: boolean) => void;
}

const AiRiskModal: React.FC<AiRiskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onAccept = () => {
    setLoading(true);

    ContentEditAPI.doOptIn(OptInNames.aiContentEdit)
      .then(async (resp) => {
        const { data } = await resp.json();
        const newOptIns = JSON.parse(data);
        dispatch(updateOptIns(newOptIns));

        onClose();
        onSuccess(true);
      })
      .catch(async (err) => {
        let errorMsg = 'An unexpected error occurred.';
        // If err is a Response object (e.g., from fetch), try to parse its body
        if (err && typeof err.json === 'function') {
          try {
            const errorData = await err.json();
            errorMsg = errorData?.error || errorMsg;
          } catch (jsonErr) {
            // fallback to default error message
          }
        } else if (err?.message) {
          errorMsg = err.message;
        }
        setError(errorMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <ModalDialog
      className="z-1050"
      topOffsetClass="top-40"
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <h2 className="font-bold text-2xl mb-8 pr-4 break-normal">
        Ready to get started?
      </h2>
      <div className="space-y-6">
        {/* Main description text */}
        <p className="text-sm font-normal text-black leading-relaxed">
          By continuing, you confirm that you will not use the service to:
        </p>

        {/* Bullet point list */}
        <ul className="text-sm font-normal text-black leading-relaxed">
          <li className="flex">
            <span className="mr-2">•</span>
            <span>
              Generate content that threatens child safety or depicts real
              violence, exploitation, or harassment.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2">•</span>
            <span>
              Create or distribute hateful, extremist, or election-related
              misinformation.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2">•</span>
            <span>
              Build or train competing AI models or attempt to disrupt the
              service.
            </span>
          </li>
          <li className="flex">
            <span className="mr-2">•</span>
            <span>Support harmful, abusive, or illegal purposes.</span>
          </li>
          <li className="flex">
            <span className="mr-2">•</span>
            <span>
              Upload content that infringes on others' rights or unlawful
              material.
            </span>
          </li>
        </ul>

        {error && <span className="text-sm text-red-700">Error: {error}</span>}
        {/* Footer with Accept button */}
        <div className="flex justify-end mt-6">
          <Button
            variant="primary"
            onClick={onAccept}
            disabled={loading}
            className="cursor-pointer"
            buttonClassName="rounded-full bg-yellow-500 hover:bg-yellow-600 py-2 px-8 font-bold"
          >
            {loading ? 'Pending...' : 'Accept'}
          </Button>
        </div>
      </div>
    </ModalDialog>
  );
};

AiRiskModal.displayName = 'AiRiskModal';

export default AiRiskModal;
